import { useEffect, useMemo, useRef } from "react";
import type React from "react";
import { createLongPress, type PressPoint } from "@/lib/long-press";

/**
 * Hold a finger on an element to open its menu (`lib/long-press.ts` decides). Spread
 * the handlers on the element. It takes the native context menu too: with
 * `contextMenu` on a desktop right-click opens the same menu, and either way the
 * browser's own is refused (Android's long-press menu; iOS's callout is CSS:
 * `select-none [-webkit-touch-callout:none]`).
 *
 * The click that ends a hold is swallowed in the capture phase, so a button inside
 * does not also act as a tap. A touch press is owned by the innermost element
 * holding it — pointerdown and a touch's contextmenu stop here — so a pill with its
 * own hold inside a row with one opens one menu.
 */
export function useLongPress({
  onLongPress,
  contextMenu = false,
  disabled = false,
  ms,
  slop,
  touch = true,
}: {
  onLongPress: (point: PressPoint) => void;
  /** A right-click opens it too. */
  contextMenu?: boolean;
  disabled?: boolean;
  /**
   * A finger holds here. Off for an element `useTouchLiftDrag` lifts: its one
   * machine opens the menu on a still release, and a second timer here would open
   * it under a drag. A right-click still opens it.
   */
  touch?: boolean;
  ms?: number;
  slop?: number;
}) {
  const latest = useRef(onLongPress);
  latest.current = onLongPress;
  const press = useMemo(
    () =>
      createLongPress({
        ms,
        slop,
        onLongPress: (point) => latest.current(point),
        schedule: (fire, delay) => {
          const handle = setTimeout(fire, delay);
          return () => clearTimeout(handle);
        },
        buzz: () => navigator.vibrate?.(10),
      }),
    [ms, slop],
  );
  useEffect(() => () => press.end(), [press]);

  const handlers = {
    onPointerDown: (event: React.PointerEvent) => {
      if (disabled) return;
      if (!touch && event.pointerType !== "mouse") return;
      event.stopPropagation();
      press.down(event);
    },
    onPointerMove: (event: React.PointerEvent) => press.move(event),
    onPointerUp: () => press.end(),
    onPointerCancel: () => press.end(),
    onPointerLeave: () => press.end(),
    onContextMenu: (event: React.MouseEvent) => {
      event.preventDefault();
      if (disabled) return;
      const pointerType = (event.nativeEvent as Partial<PointerEvent>).pointerType ?? "mouse";
      const byTouch = pointerType === "touch" || pointerType === "pen";
      if (byTouch && !touch) {
        event.stopPropagation();
        return;
      }
      if (!contextMenu) {
        // Android sends this at its own long press: the hold already timing is the menu,
        // and it is this element's. A right-click goes on up, to a menu that wants it.
        if (byTouch) event.stopPropagation();
        return;
      }
      event.stopPropagation();
      press.contextMenu({ pointerType, clientX: event.clientX, clientY: event.clientY });
    },
    onClickCapture: (event: React.MouseEvent) => {
      if (!press.swallowClick()) return;
      event.preventDefault();
      event.stopPropagation();
    },
  };
  return handlers;
}
